// Field access for the `users` collection.
//
// Users are written by the app rather than by admin, so the schema is not
// defined anywhere else in this repo. Confirmed top-level fields:
//
//   uid, name, email, first_seen, last_seen, shopping_list_id,
//   accepted_email_consent, accepted_push_consent,
//   preference_main_goal, aliases (merged RevenueCat profiles),
//   subscriptions (map of product id -> subscription)
//
// Subscription entries follow the RevenueCat subscriber shape. The readers
// below still fall back through alternative spellings so a rename in the app
// degrades to a blank field rather than a broken page.

function pick(source, keys) {
  if (!source || typeof source !== 'object') return undefined
  for (const key of keys) {
    const value = source[key]
    if (value !== undefined && value !== null && value !== '') return value
  }
  return undefined
}

// "accepted_email_consent" -> "Accepted email consent"
export function humanise(key) {
  const words = String(key)
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .trim()
    .toLowerCase()
  return words.charAt(0).toUpperCase() + words.slice(1)
}

// Firestore Timestamp, millis, seconds, ISO string or Date -> Date
export function toDate(value) {
  if (!value) return null
  if (value.toDate) return value.toDate()
  if (value instanceof Date) return value
  if (typeof value === 'number') {
    // RevenueCat timestamps are sometimes seconds rather than millis
    return new Date(value < 1e11 ? value * 1000 : value)
  }
  const parsed = new Date(value)
  return isNaN(parsed.getTime()) ? null : parsed
}

export function formatDate(value) {
  const date = toDate(value)
  if (!date) return '—'
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
}

export function formatDateTime(value) {
  const date = toDate(value)
  if (!date) return '—'
  return date.toLocaleString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
  })
}

export function userName(user) {
  return pick(user, ['name', 'display_name', 'displayName', 'full_name']) || ''
}

export function userEmail(user) {
  return pick(user, ['email', 'email_address']) || ''
}

export function userUid(user, fallbackId) {
  return pick(user, ['uid', 'user_id', 'userId']) || fallbackId || ''
}

// first_seen is the closest thing the app records to a signup date
export function userCreatedAt(user) {
  return pick(user, ['first_seen', 'firstSeen', 'created_at', 'createdAt'])
}

export function userLastSeen(user) {
  return pick(user, ['last_seen', 'lastSeen'])
}

export function userAliases(user) {
  const aliases = pick(user, ['aliases'])
  if (!aliases) return []
  return Array.isArray(aliases) ? aliases : [aliases]
}

export function shoppingListId(user) {
  return pick(user, ['shopping_list_id']) || ''
}

export function mainGoal(user) {
  return pick(user, ['preference_main_goal', 'preferenceMainGoal', 'main_goal', 'goal'])
}

// Consent is stored as flat accepted_*_consent booleans
const CONSENT_KEYS = [
  'accepted_email_consent',
  'accepted_push_consent',
  'accepted_sms_consent',
  'accepted_analytics_consent',
  'accepted_marketing_consent'
]

export function marketingConsentRows(user) {
  if (!user) return []
  const rows = CONSENT_KEYS
    .filter(key => user[key] !== undefined && user[key] !== null)
    .map(key => ({ label: humanise(key), value: user[key] }))

  // Surface any other consent-ish field the app starts writing
  Object.keys(user).forEach(key => {
    if (CONSENT_KEYS.includes(key)) return
    if (!/consent|opt_in|optIn|marketing/i.test(key)) return
    const value = user[key]
    if (value === undefined || value === null || typeof value === 'object') return
    rows.push({ label: humanise(key), value })
  })

  return rows
}

// `subscriptions` is a map of product id -> RevenueCat subscription entry.
// Returns a normalised array, most recently expiring first.
export function subscriptionList(user) {
  const map = pick(user, ['subscriptions', 'subscription'])
  if (!map || typeof map !== 'object') return []

  return Object.keys(map)
    .map(productId => normaliseSubscription(productId, map[productId]))
    .filter(Boolean)
    .sort((a, b) => b.expiresSort - a.expiresSort)
}

function normaliseSubscription(productId, sub) {
  if (!sub || typeof sub !== 'object') return null

  const periodType = pick(sub, ['period_type', 'periodType'])
  const expiresDate = pick(sub, [
    'expires_date', 'expiresDate', 'expires_at', 'renews_at', 'current_period_end', 'expiry'
  ])
  const unsubscribeDetectedAt = pick(sub, [
    'unsubscribe_detected_at', 'unsubscribeDetectedAt', 'cancelled_at', 'canceled_at'
  ])
  const gracePeriodExpiresAt = pick(sub, ['grace_period_expires_date', 'gracePeriodExpiresDate'])

  let isTrial = pick(sub, ['is_trial', 'isTrial', 'in_trial'])
  if (isTrial === undefined) {
    isTrial = periodType ? String(periodType).toLowerCase() === 'trial' : undefined
  }

  // RevenueCat has no will-renew flag: an unsubscribe timestamp is the signal.
  let willRenew = pick(sub, ['will_renew', 'willRenew', 'auto_renew', 'is_auto_renewing'])
  if (typeof willRenew !== 'boolean') {
    willRenew = expiresDate ? !unsubscribeDetectedAt : undefined
  }

  const expiryDate = toDate(gracePeriodExpiresAt) || toDate(expiresDate)
  // No expiry at all means a lifetime / non-expiring entitlement
  const isActive = expiryDate ? expiryDate.getTime() > Date.now() : true

  return {
    productId,
    periodType,
    isTrial,
    willRenew,
    isActive,
    store: pick(sub, ['store', 'platform', 'source']),
    purchaseDate: pick(sub, ['purchase_date', 'purchaseDate', 'started_at', 'start_date']),
    originalPurchaseDate: pick(sub, ['original_purchase_date', 'originalPurchaseDate']),
    expiresDate,
    unsubscribeDetectedAt,
    gracePeriodExpiresAt,
    billingIssuesDetectedAt: pick(sub, ['billing_issues_detected_at', 'billingIssuesDetectedAt']),
    isSandbox: pick(sub, ['is_sandbox', 'isSandbox']),
    // expiryDate folds in the grace period; expiresDate is the raw field
    expiryDate,
    expiresSort: expiryDate ? expiryDate.getTime() : Number.MAX_SAFE_INTEGER
  }
}

export function hasMembership(user) {
  return subscriptionList(user).some(sub => sub.isActive)
}

// The full set of membership states, in the order they are worth grouping by.
// The index doubles as the table sort rank, and every state is offered as a
// filter option, so this list is the single source of truth for the three
// places the list view describes membership.
export const MEMBERSHIP_STATES = [
  { key: 'lifetime', group: 'Members', filterLabel: 'Lifetime',
    label: 'Lifetime', tone: 'is-lifetime', detail: '' },
  { key: 'renewing', group: 'Members', filterLabel: 'Member · renewing',
    label: 'Member', tone: 'is-member', detail: 'Renews', detailTone: 'is-renewing' },
  { key: 'cancelling', group: 'Members', filterLabel: 'Member · cancelling',
    label: 'Member', tone: 'is-member', detail: 'Cancels', detailTone: 'is-cancelling' },
  { key: 'member', group: 'Members', filterLabel: 'Member · renewal unknown',
    label: 'Member', tone: 'is-member', detail: '' },
  { key: 'trialRenewing', group: 'Trials', filterLabel: 'Trial · renewing',
    label: 'Trial', tone: 'is-trial', detail: 'Renews', detailTone: 'is-renewing' },
  { key: 'trialCancelling', group: 'Trials', filterLabel: 'Trial · cancelling',
    label: 'Trial', tone: 'is-trial', detail: 'Cancels', detailTone: 'is-cancelling' },
  { key: 'trial', group: 'Trials', filterLabel: 'Trial · renewal unknown',
    label: 'Trial', tone: 'is-trial', detail: '' },
  { key: 'expired', group: 'Other', filterLabel: 'Expired',
    label: 'Expired', tone: 'is-expired', detail: '' },
  { key: 'free', group: 'Other', filterLabel: 'Free',
    label: 'Free', tone: 'is-free', detail: '' }
]

const STATES_BY_KEY = MEMBERSHIP_STATES.reduce((map, state, index) => {
  map[state.key] = { ...state, rank: index }
  return map
}, {})

export function membershipState(key) {
  return STATES_BY_KEY[key] || STATES_BY_KEY.free
}

// Keys rolled up for the summary counts above the table. Each entry is also a
// filter value, so a count can be clicked to narrow the table to it.
// `quickLabel` marks the roll-ups worth offering in the filter dropdown too.
export const MEMBERSHIP_SUMMARY = [
  { label: 'members', filter: 'group:members', quickLabel: 'Any member', tone: 'is-member',
    keys: ['lifetime', 'renewing', 'cancelling', 'member'] },
  { label: 'on trial', filter: 'group:trials', quickLabel: 'Any trial', tone: 'is-trial',
    keys: ['trialRenewing', 'trialCancelling', 'trial'] },
  { label: 'cancelling', filter: 'group:cancelling', quickLabel: 'Cancelling (member or trial)',
    tone: 'is-cancelling', keys: ['cancelling', 'trialCancelling'] },
  { label: 'expired', filter: 'expired', tone: 'is-expired', keys: ['expired'] },
  { label: 'free', filter: 'free', tone: 'is-free', keys: ['free'] }
]

// A filter value is either a roll-up ("group:members") or a single state key
export function matchesMembershipFilter(key, filter) {
  if (!filter) return true
  const group = MEMBERSHIP_SUMMARY.find(entry => entry.filter === filter)
  return group ? group.keys.includes(key) : key === filter
}

// A one-line reading of where a user stands: free, trialling, paying, lapsed,
// and — while a subscription is running — whether it is set to renew or to
// cancel at the end of the current period.
export function membershipStatus(user) {
  return { ...membershipState(membershipKey(user)), expiryDate: membershipExpiry(user) }
}

function decidingSubscription(user) {
  const subs = subscriptionList(user)
  if (!subs.length) return null
  // The entry that decides the user's standing: the active one running
  // longest, or failing that the one that lapsed most recently.
  return subs.find(entry => entry.isActive) || subs[0]
}

function membershipExpiry(user) {
  const sub = decidingSubscription(user)
  return sub && sub.expiryDate ? sub.expiryDate : null
}

function membershipKey(user) {
  const sub = decidingSubscription(user)
  if (!sub) return 'free'
  if (!sub.isActive) return 'expired'
  // Active with no expiry at all is a lifetime / non-expiring entitlement
  if (!sub.expiryDate) return 'lifetime'

  const isTrial = sub.isTrial === true
  if (sub.willRenew === true) return isTrial ? 'trialRenewing' : 'renewing'
  if (sub.willRenew === false) return isTrial ? 'trialCancelling' : 'cancelling'
  // Neither a will-renew flag nor a cancellation stamp to go on
  return isTrial ? 'trial' : 'member'
}

// RevenueCat mints an anonymous profile the first time the app is opened,
// before the user has an account. Once they sign up, the real profile lists
// that id under `aliases` and the anonymous doc stops being written to, so it
// lingers as a stale duplicate.
export function isAnonymousId(id) {
  return /^\$?RCAnonymous/i.test(String(id || ''))
}

// Two ways a profile can belong to nobody yet: the app signed the user in with
// Firebase anonymous auth and flagged the doc, or RevenueCat minted the
// profile before any sign-in at all. An explicit flag always wins over the
// id-shape guess, so the app can mark a profile as claimed.
const ANONYMOUS_FLAGS = ['is_anonymous', 'isAnonymous', 'anonymous', 'is_anon']

export function isAnonymousUser(user, fallbackId) {
  const flag = pick(user, ANONYMOUS_FLAGS)
  if (typeof flag === 'boolean') return flag
  if (typeof flag === 'string') return flag.toLowerCase() === 'true'
  return isAnonymousId(userUid(user, fallbackId)) || isAnonymousId(fallbackId)
}

export const MIXPANEL_PROFILE_BASE =
  'https://eu.mixpanel.com/project/2777035/view/3312064/app/profile#distinct_id='

export function mixpanelProfileUrl(uid) {
  return uid ? MIXPANEL_PROFILE_BASE + encodeURIComponent(uid) : ''
}
